export const DEFAULT_MAX_LIFESPAN_YEARS = 60;

export const CARE_GUIDE_DIET_TRAIT_OPTIONS = [
  { value: "eats_meat", label: "Eats Meat" },
  { value: "eats_plants", label: "Eats Plants" },
  { value: "photosynthetic", label: "Photosynthetic" },
  { value: "detritus_scavenger", label: "Detritus / Scavenger" },
];

export const CARE_GUIDE_DIET_TRAIT_LABELS = CARE_GUIDE_DIET_TRAIT_OPTIONS.reduce(
  (labels, option) => ({
    ...labels,
    [option.value]: option.label,
  }),
  {}
);

const NUMBER_REGEX = /\d+(?:\.\d+)?/g;
const RANGE_REGEX = /(\d+(?:\.\d+)?)\s*(?:-|–|—|to)\s*(\d+(?:\.\d+)?)/i;
const PLUS_REGEX = /(\d+(?:\.\d+)?)\s*\+/;

const normalizeTextValue = (value) => String(value || "").trim().toLowerCase();

export function extractNumericValues(value) {
  if (!value) return [];
  return Array.from(String(value).matchAll(NUMBER_REGEX)).map((match) =>
    Number.parseFloat(match[0])
  );
}

export function getMaxNumericValueInText(value) {
  const numericValues = extractNumericValues(value);
  if (numericValues.length === 0) return null;
  return Math.max(...numericValues);
}

export function getCareGuideDietTraits(rawDiet) {
  const normalizedDiet = normalizeTextValue(rawDiet);
  if (!normalizedDiet) return [];

  const traits = new Set();

  if (
    normalizedDiet.includes("carnivore") ||
    normalizedDiet.includes("planktivore")
  ) {
    traits.add("eats_meat");
  }

  if (normalizedDiet.includes("omnivore")) {
    traits.add("eats_meat");
    traits.add("eats_plants");
  }

  if (normalizedDiet.includes("herbivore")) {
    traits.add("eats_plants");
  }

  if (normalizedDiet.includes("photosynthetic")) {
    traits.add("photosynthetic");
  }

  if (normalizedDiet.includes("detritivore")) {
    traits.add("detritus_scavenger");
  }

  return CARE_GUIDE_DIET_TRAIT_OPTIONS.map((option) => option.value).filter(
    (value) => traits.has(value)
  );
}

export function parseCareGuideLifespanRange(
  rawLifespan,
  upperBound = DEFAULT_MAX_LIFESPAN_YEARS
) {
  const normalizedLifespan = normalizeTextValue(rawLifespan);
  if (!normalizedLifespan) return null;

  const rangeMatch = normalizedLifespan.match(RANGE_REGEX);
  if (rangeMatch) {
    const minValue = Number.parseFloat(rangeMatch[1]);
    let maxValue = Number.parseFloat(rangeMatch[2]);

    if (normalizedLifespan.includes("+")) {
      maxValue = Math.max(maxValue, upperBound);
    }

    if (minValue <= maxValue) {
      return [minValue, maxValue];
    }

    return [maxValue, minValue];
  }

  const plusMatch = normalizedLifespan.match(PLUS_REGEX);
  if (plusMatch) {
    const minValue = Number.parseFloat(plusMatch[1]);
    return [minValue, upperBound];
  }

  const numericValues = extractNumericValues(normalizedLifespan);
  if (numericValues.length === 1) {
    return [numericValues[0], numericValues[0]];
  }

  if (normalizedLifespan.includes("decade")) {
    if (numericValues.length > 0) {
      return [numericValues[0], upperBound];
    }
    return [10, upperBound];
  }

  if (normalizedLifespan.includes("indefinite")) {
    return [10, upperBound];
  }

  if (normalizedLifespan.includes("many years")) {
    return [5, upperBound];
  }

  return null;
}
